import { ActiveItemWrapper } from '@/components/features/Editor/components/ActiveItemWrapper';
import { type SectionProps } from '@/components/features/Editor/utils';
import { AspectRatio, Box, Flex } from '@chakra-ui/react';
import { useMotionValueEvent, useScroll } from 'framer-motion';
import dynamic from 'next/dynamic';
import { rem } from 'polished';
import { Fragment, useRef, useState, type RefObject } from 'react';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface Props extends SectionProps {
  isPreview?: boolean;
  activeItem?: 'video';
  containerRef?: RefObject<HTMLElement>;
}

export const VideoBlock = ({ video, isPreview, activeItem, containerRef }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const scrollProgressContainer = useRef(null);

  const { scrollYProgress } = useScroll({
    target: scrollProgressContainer,
    container: containerRef ? containerRef : undefined,
  });

  useMotionValueEvent(scrollYProgress, 'change', scrollPos =>
    setExpanded(!!scrollPos && scrollPos < 1),
  );

  if (isPreview)
    return (
      <Box px="space.120" py="space.96" bg="black">
        <ActiveItemWrapper isActive={activeItem === 'video'} isPreview>
          <AspectRatio ratio={16 / 9} pointerEvents="none">
            {video?.src ? <ReactPlayer height="100%" width="100%" url={video.src} /> : <Fragment />}
          </AspectRatio>
        </ActiveItemWrapper>
      </Box>
    );

  return (
    <Box
      pos="relative"
      bg="black"
      h={{ md: '250vh' }}
      py={{ base: 'space.40', md: 'unset' }}
      ref={scrollProgressContainer}
    >
      <Flex
        justify="center"
        align="center"
        transition="padding 450ms ease-in-out"
        pos="sticky"
        top={0}
        h={{ md: '100vh' }}
      >
        <Box
          display={{ base: 'none', md: 'block' }}
          w={expanded ? '100%' : `calc(100% - ${rem(220)})`}
          h={expanded ? '100%' : `calc(100% - ${rem(196)})`}
          transition="all 450ms ease-in-out"
        >
          {video?.src && (
            <ReactPlayer playing={expanded} height="100%" width="100%" url={video.src} muted />
          )}
        </Box>
        <AspectRatio display={{ md: 'none' }} boxSize="100%" ratio={16 / 9}>
          {video?.src ? (
            <ReactPlayer playing={expanded} height="100%" width="100%" url={video.src} muted />
          ) : (
            <Fragment />
          )}
        </AspectRatio>
      </Flex>
    </Box>
  );
};
